import { useMediaQuery } from '@mantine/hooks';
import breakpoints from 'common/constants/breakpoints';
import ActionIcon from 'mantine-components/widget/action-icon';
import Button, { ButtonProps } from 'mantine-components/widget/button';
import Menu, { MenuDataType } from 'mantine-components/widget/menu';
import Tooltip from 'mantine-components/widget/tooltip';
import useTranslation from 'next-translate/useTranslation';
import React from 'react';

import { UniskaiButtonType, generateUniskaiActionAsset } from './utils';

export type RawButtonProps = Omit<
  ButtonProps,
  'size' | 'styles' | 'classNames' | 'type' | 'vars'
>;

export interface UniskaiButtonProps extends RawButtonProps {
  type: UniskaiButtonType;
  color?: ButtonProps['color'];
  variant?: ButtonProps['variant'];
  isLite?: boolean;
  isResponsive?: boolean;
  actions?: MenuDataType[];
  // for permission
  show?: boolean;
}

export default function UniskaiButton(props: UniskaiButtonProps) {
  const {
    type,
    isLite = false,
    color,
    variant,
    isResponsive = true,
    actions = [],
    show = true,
    ...rest
  } = props;
  const isMobile = useMediaQuery(breakpoints.screenMaxSm);
  const { t } = useTranslation();

  const hasMenu = actions.filter((item) => item.isHidden === false).length > 0;

  const _props = React.useMemo<RawButtonProps>(() => {
    return generateUniskaiActionAsset(type, t);
  }, [t, type]);

  const component = React.useMemo(() => {
    if ((isLite || isMobile) && isResponsive) {
      return (
        <Tooltip label={_props.children}>
          <ActionIcon
            {...rest}
            color={color || _props.color}
            variant={variant || _props.variant}
          >
            {_props.rightSection || _props.leftSection}
          </ActionIcon>
        </Tooltip>
      );
    }

    return (
      <Button
        {...rest}
        {..._props}
        color={color || _props.color}
        variant={variant || _props.variant}
        disabled={props.loading || rest.disabled}
        fullWidth={hasMenu || rest.fullWidth || _props.fullWidth}
      />
    );
  }, [
    _props,
    color,
    hasMenu,
    isLite,
    isMobile,
    isResponsive,
    props.loading,
    rest,
    variant,
  ]);

  if (show === false) return null;

  if (actions.filter((item) => item.isHidden === false).length > 0) {
    return <Menu data={actions} target={component} />;
  }

  return component;
}
